import * as React from 'react'
import { Image, Text, TouchableOpacity, View, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Button from '@components/button/Button'
import SmallButton from '@components/button/SmallButton'
import Checkbox from '@components/checkbox/Checkbox'
import SimpleAlert from '@components/modal/SimpleAlert'
import Root from '@components/root/Root'
import Steps from '@components/steps/Steps'
import { ModalListService } from '@components/modal/ModalContact'

import useReducer from '@store/useReducer'
import * as UserStore from '@store/user/user'
import * as EdlStore from '@store/edl/edl'
import * as ContactsStore from '@store/contacts/contacts'

import api from '@api/api'
import Logger from '@utils/logger'

import scheme from '../scheme'

import useI18n from '@i18n/useI18n'

import { allStepsEDL } from '@screens/edl/utils'

const ViewGroupedLayout = ({ title, type, form, onSceneChange, onBack, onFinish }: SignatureViewLayoutProps) => {
  const i18n = useI18n()

  const user = useReducer(UserStore.store, (s) => s.user)
  const contacts = useReducer(ContactsStore.store, (s) => s.contacts)

  const [valid, setValid] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [signature, setSignature] = React.useState<FileDetailResponse>()

  const steps: Step[] = allStepsEDL(3, i18n)

  React.useEffect(() => {
    scheme.edl
      .validate(form)
      .then(() => setValid(true))
      .catch(() => setValid(false))
  }, [form])

  React.useEffect(() => {
    if (user && user.signatureFileId) {
      api.files.get(user.signatureFileId).then(setSignature).catch(Logger.error)
    }
  }, [user])

  const onSignaturePref = () => {
    EdlStore.actions.setForm({ utilisateurInitiateurSignature: user && user.signatureFileId, usePrefSignature: true })
  }

  const openViparisActions = () =>
    SimpleAlert.open({
      columnButtons: true,
      description: i18n.t('screens.edlSignature.bottomAlert.description'),
      buttons: [
        {
          label: i18n.t('screens.edlSignature.bottomAlert.manual'),
          onClick: () => {
            onSceneChange('VIPARIS_SIGNATURE')
            SimpleAlert.close()
          },
        },
        {
          disabled: !user || !user.signatureFileId,
          label: i18n.t('screens.edlSignature.bottomAlert.saved'),
          onClick: () => {
            onSignaturePref()
            SimpleAlert.close()
          },
        },
        { label: i18n.t('common.cancel'), onClick: () => SimpleAlert.close(), color: 'light' },
      ],
    })

  const openOrganizerActions = () =>
    SimpleAlert.open({
      columnButtons: true,
      description: i18n.t('screens.edlSignature.bottomAlert.description'),
      buttons: [
        {
          disabled: !!form.clientAbsent,
          label: i18n.t('screens.edlSignature.bottomAlert.manual'),
          onClick: () => {
            onSceneChange('FORM_SIGNATURE')
            SimpleAlert.close()
          },
        },
        { label: i18n.t('common.cancel'), onClick: () => SimpleAlert.close(), color: 'light' },
      ],
    })

  const onValidate = () => {
    setLoading(true)
    onFinish().then(() => setLoading(false))
  }

  const renderViparis = () => {
    return (
      <>
        <Title>
          {i18n.t('screens.edlSignature.viparisContact', { name: user ? `${user.prenom} ${user.nom}` : '' })}
        </Title>
        <Card>
          {(form.usePrefSignature && signature) || (!form.usePrefSignature && form.utilisateurInitiateurSignature) ? (
            <SignatureContainer disabled={loading} activeOpacity={0.9} onPress={openViparisActions}>
              <Signature
                source={{
                  uri: form.usePrefSignature && signature ? signature.url : form.utilisateurInitiateurSignature,
                }}
                resizeMode="contain"
              />
            </SignatureContainer>
          ) : (
            <SignatureContainer disabled={loading} activeOpacity={0.9} onPress={openViparisActions} />
          )}
        </Card>

        {!!form.utilisateurInitiateurSignature && !form.usePrefSignature && (
          <Select>
            <Checkbox
              label={i18n.t(`screens.edlSignature.signatureToSavedShort`)}
              disabled={loading}
              onClick={() => EdlStore.actions.setForm({ rememberedSignature: !form.rememberedSignature })}
              value={!!form.rememberedSignature}
            />
          </Select>
        )}
      </>
    )
  }

  const renderOrganizer = () => {
    return (
      <>
        <Title>
          {!!form.contactOrganisateur
            ? i18n.t('screens.edlSignature.organisatorName', {
                name: `${form.contactOrganisateur.prenom} ${form.contactOrganisateur.nom}`,
              })
            : i18n.t('screens.edlSignature.organisator', { name: user ? `${user.prenom} ${user.nom}` : '' })}
        </Title>
        <Card>
          {!form.clientAbsent && form.contactOrganisateur && form.contactOrganisateur.signatureFileUrl ? (
            <SignatureContainer disabled={loading} activeOpacity={0.9} onPress={openOrganizerActions}>
              <Signature source={{ uri: form.contactOrganisateur.signatureFileUrl }} resizeMode="contain" />
            </SignatureContainer>
          ) : (
            <SignatureContainer disabled={loading} activeOpacity={0.9} onPress={openOrganizerActions} />
          )}
        </Card>

        <Select>
          <Checkbox
            label={i18n.t(`screens.edlSignature.clientMissing`)}
            disabled={loading}
            onClick={() =>
              EdlStore.actions.setForm({ contactOrganisateur: undefined, clientAbsent: !form.clientAbsent })
            }
            value={!!form.clientAbsent}
          />
        </Select>
      </>
    )
  }

  const onSelectContacts = () => {
    const contactsData = contacts.map((c) => ({ label: `${c.prenom} ${c.nom} (${c.email})`, value: c }))

    const formData = form.contacts
      ? form.contacts.filter((c) => !c.id).map((c) => ({ label: `${c.prenom} ${c.nom} (${c.email})`, value: c }))
      : []

    ModalListService.open(
      {
        data: [...contactsData, ...formData].sort((a, b) => a.label.localeCompare(b.label)),
        onItemSelected: (contacts) => EdlStore.actions.setForm({ contacts }),
        title: i18n.t('screens.edlSignature.contacts.title'),
        value: form.contacts || [],
      },
      true
    )
  }

  return (
    <Root fixed header={title} renderSubHeader={() => <Steps steps={steps} />} onLeft={onBack}>
      <Description>
        {i18n.t('screens.edlSignature.description', { type: i18n.t(`screens.edlRecap.${type}`) })}
      </Description>

      <MainContainer>
        {renderViparis()}
        {renderOrganizer()}

        <Line>
          <ContactsInfo>{i18n.t('screens.edlSignature.contacts.sendToContacts')}</ContactsInfo>
          <SmallButton label={i18n.t('common.select')} status="active" onClick={onSelectContacts} />
        </Line>
        <ContactsCount>
          {form.contacts && form.contacts.length > 0
            ? i18n.t('screens.edlSignature.contacts.count', { count: form.contacts.length })
            : i18n.t('screens.edlSignature.contacts.noContact')}
        </ContactsCount>
      </MainContainer>

      <Buttons>
        <ButtonContainer>
          <Button
            textOnly
            textColor={theme.colors.primary}
            label={i18n.t('screens.edl.spaces.back')}
            status="active"
            onClick={onBack}
          />
        </ButtonContainer>
        <ButtonContainer>
          <Button
            label={i18n.t('common.finish')}
            status={loading ? 'loading' : valid ? 'active' : 'disabled'}
            onClick={onValidate}
          />
        </ButtonContainer>
      </Buttons>
    </Root>
  )
}

export default ViewGroupedLayout

const MainContainer = styled(ScrollView)`
  flex: 1;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  margin: 16px;
  margin-bottom: 8px;
`

const Card = styled(View)`
  margin: 0 16px;
  margin-bottom: 16px;
  background-color: ${(props) => props.theme.colors.background};
`

const SignatureContainer = styled(TouchableOpacity)`
  height: 150px;
`

const Signature = styled(Image)`
  height: 150px;
`

const Buttons = styled(View)`
  flex-direction: row;
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)`
  flex: 1;
  margin: 12px;
`

const Select = styled(View)`
  padding: 5px 16px;
  background-color: ${(props) => props.theme.colors.background};
  border: 0 solid ${(props) => props.theme.colors.backgroundTertiary};
  border-top-width: 0.5px;
  border-bottom-width: 0.5px;
`

const Description = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.tertiary};
  margin: 16px;
`

const ContactsInfo = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
`

const ContactsCount = styled(Text)`
  padding: 0 12px 24px;
  ${(props) => props.theme.fonts.Footnote};
`

const Line = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
`
